<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- 工具栏 -->
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h4 class="card-title mb-0">{{ $t("menuitems.setting.apicredentials") }}</h4>
              <el-button type="primary" icon="el-icon-plus" @click="handleCreate">
                {{ $t("apicredentials.create") }}
              </el-button>
            </div>

            <!-- 凭证列表 -->
            <el-table
              v-loading="loading"
              :data="credentials"
              style="width: 100%"
              border
            >
              <el-table-column prop="name" :label="$t('apicredentials.name')" min-width="150" />
              <el-table-column prop="api_key" :label="$t('apicredentials.api_key')" min-width="200">
                <template slot-scope="scope">
                  <span class="text-muted">{{ scope.row.api_key }}</span>
                  <el-button
                    type="text"
                    size="mini"
                    icon="el-icon-copy-document"
                    @click="copyToClipboard(scope.row.api_key)"
                    style="margin-left: 10px;"
                  >
                    {{ $t('apicredentials.copy') }}
                  </el-button>
                </template>
              </el-table-column>
              <el-table-column prop="status" :label="$t('apicredentials.status')" width="100" align="center">
                <template slot-scope="scope">
                  <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'" size="small">
                    {{ scope.row.status === 1 ? $t('apicredentials.enabled') : $t('apicredentials.disabled') }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="last_used_at" :label="$t('apicredentials.last_used')" width="180">
                <template slot-scope="scope">
                  {{ scope.row.last_used_at || $t('apicredentials.never_used') }}
                </template>
              </el-table-column>
              <el-table-column prop="created_at" :label="$t('apicredentials.created_at')" width="180" />
              <el-table-column prop="expires_at" :label="$t('apicredentials.expires_at')" width="180">
                <template slot-scope="scope">
                  {{ scope.row.expires_at || $t('apicredentials.never_expires') }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('dns.record.action')" width="250" fixed="right">
                <template slot-scope="scope">
                  <el-button
                    type="text"
                    size="mini"
                    @click="handleView(scope.row)"
                  >
                    {{ $t('apicredentials.view') }}
                  </el-button>
                  <el-button
                    type="text"
                    size="mini"
                    @click="handleEdit(scope.row)"
                  >
                    {{ $t('apicredentials.edit') }}
                  </el-button>
                  <el-button
                    type="text"
                    size="mini"
                    :class="scope.row.status === 1 ? 'text-danger' : 'text-success'"
                    @click="handleToggleStatus(scope.row)"
                  >
                    {{ scope.row.status === 1 ? $t('apicredentials.disable') : $t('apicredentials.enable') }}
                  </el-button>
                  <el-button
                    type="text"
                    size="mini"
                    class="text-danger"
                    @click="handleDelete(scope.row)"
                  >
                    {{ $t('apicredentials.delete') }}
                  </el-button>
                </template>
              </el-table-column>
            </el-table>

            <!-- 空状态 -->
            <div v-if="!loading && credentials.length === 0" class="text-center py-5">
              <i class="el-icon-document-remove" style="font-size: 48px; color: #ccc;"></i>
              <p class="text-muted mt-3">{{ $t('apicredentials.empty') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 创建/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px"
      @close="handleDialogClose"
    >
      <el-form
        ref="credentialForm"
        :model="credentialForm"
        :rules="credentialRules"
        label-width="120px"
      >
        <el-form-item :label="$t('apicredentials.name')" prop="name">
          <el-input v-model="credentialForm.name" :placeholder="$t('apicredentials.placeholder.name')" />
        </el-form-item>
        <el-form-item :label="$t('apicredentials.description')" prop="description">
          <el-input
            v-model="credentialForm.description"
            type="textarea"
            :rows="3"
            :placeholder="$t('apicredentials.placeholder.description')"
          />
        </el-form-item>
        <el-form-item :label="$t('apicredentials.expires_at')" prop="expires_at">
          <el-date-picker
            v-model="credentialForm.expires_at"
            type="datetime"
            :placeholder="$t('apicredentials.expires_at') + '（可选，留空表示永不过期）'"
            format="yyyy-MM-dd HH:mm:ss"
            value-format="timestamp"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item :label="$t('apicredentials.ip_whitelist')" prop="ip_whitelist">
          <el-input
            v-model="credentialForm.ip_whitelist"
            type="textarea"
            :rows="2"
            :placeholder="$t('apicredentials.placeholder.ip_whitelist')"
          />
          <small class="text-muted">{{ $t('apicredentials.placeholder.ip_example') }}</small>
        </el-form-item>
        <el-form-item :label="$t('apicredentials.rate_limit')" prop="rate_limit">
          <el-input-number
            v-model="credentialForm.rate_limit"
            :min="1"
            :max="10000"
            :placeholder="$t('apicredentials.rate_limit')"
          />
          <small class="text-muted ml-2">默认：1000 次/分钟</small>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ $t('global.button.cancel') }}</el-button>
        <el-button type="primary" :loading="submitLoading" @click="handleSubmit">
          {{ $t('global.button.ok') }}
        </el-button>
      </div>
    </el-dialog>

    <!-- API Key显示对话框 -->
    <el-dialog
      :title="$t('apicredentials.api_key_created')"
      :visible.sync="apiKeyDialogVisible"
      width="600px"
    >
      <div class="alert alert-warning">
        <i class="el-icon-warning"></i>
        <strong>{{ $t('apicredentials.api_key_warning') }}</strong>
      </div>
      <el-form label-width="120px">
        <el-form-item :label="$t('apicredentials.api_key')">
          <el-input
            :value="newApiKey"
            readonly
            type="textarea"
            :rows="3"
          >
            <template slot="append">
              <el-button
                icon="el-icon-copy-document"
                @click="copyToClipboard(newApiKey)"
              >
                {{ $t('apicredentials.copy') }}
              </el-button>
            </template>
          </el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="apiKeyDialogVisible = false">
          {{ $t('apicredentials.saved') }}
        </el-button>
      </div>
    </el-dialog>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { authMixin, deviceMixin } from "@/mixins";
import { apiCredentialService } from "@/services/api.service";
import { requireAuth } from "@/utils/auth";
import { apiGet, apiPost, apiPut, apiDelete } from "@/utils/request";

export default {
  page() {
    return {
      title: this.$t("menuitems.setting.apicredentials"),
      meta: [{ name: "description" }],
    };
  },
  components: { Layout, PageHeader },
  mixins: [authMixin, deviceMixin],
  data() {
    return {
      title: "",
      items: [
        {
          text: this.$t("menuitems.setting.text"),
          href: "/",
        },
        {
          text: this.$t("menuitems.setting.apicredentials"),
          active: true,
        },
      ],
      loading: false,
      credentials: [],
      dialogVisible: false,
      dialogTitle: "",
      isEdit: false,
      currentCredentialId: null,
      submitLoading: false,
      credentialForm: {
        name: "",
        description: "",
        expires_at: null,
        ip_whitelist: "",
        rate_limit: 1000,
      },
      credentialRules: {},
      apiKeyDialogVisible: false,
      newApiKey: "",
    };
  },
  mounted() {
    this.initPage();
    this.initRules();
    this.loadCredentials();
  },
  methods: {
    /**
     * 初始化页面
     */
    initPage() {
      this.title = this.$t("menuitems.setting.apicredentials");
      this.items = [
        {
          text: this.$t("menuitems.setting.text"),
          href: "/",
        },
        {
          text: this.$t("menuitems.setting.apicredentials"),
          active: true,
        },
      ];
    },

    /**
     * 初始化表单验证规则
     */
    initRules() {
      this.credentialRules = {
        name: [
          { required: true, message: this.$t('apicredentials.placeholder.name'), trigger: "blur" },
        ],
        rate_limit: [
          { required: true, message: this.$t('apicredentials.rate_limit'), trigger: "blur" },
          { type: "number", min: 1, max: 10000, message: this.$t('apicredentials.rate_limit') + "必须在1-10000之间", trigger: "blur" },
        ],
      };
    },

    /**
     * 加载凭证列表
     */
    async loadCredentials() {
      if (!requireAuth(this)) {
        return;
      }

      this.loading = true;
      try {
        const response = await apiCredentialService.getCredentials(this);
        if (response && response.status === 200) {
          this.credentials = response.data || [];
        }
      } catch (error) {
        console.error("加载API凭证列表失败:", error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * 创建凭证
     */
    handleCreate() {
      this.dialogTitle = this.$t("apicredentials.create");
      this.isEdit = false;
      this.currentCredentialId = null;
      this.credentialForm = {
        name: "",
        description: "",
        expires_at: null,
        ip_whitelist: "",
        rate_limit: 1000,
      };
      this.dialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.credentialForm) {
          this.$refs.credentialForm.clearValidate();
        }
      });
    },

    /**
     * 编辑凭证
     */
    handleEdit(row) {
      this.dialogTitle = this.$t("apicredentials.edit");
      this.isEdit = true;
      this.currentCredentialId = row.id;
      this.credentialForm = {
        name: row.name,
        description: row.description || "",
        expires_at: row.expires_at ? new Date(row.expires_at).getTime() : null,
        ip_whitelist: row.ip_whitelist || "",
        rate_limit: row.rate_limit || 1000,
      };
      this.dialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.credentialForm) {
          this.$refs.credentialForm.clearValidate();
        }
      });
    },

    /**
     * 查看凭证详情
     */
    handleView(row) {
      this.$router.push({
        name: "apicredentialdetail",
        params: { id: row.id },
      });
    },

    /**
     * 提交表单
     */
    async handleSubmit() {
      this.$refs.credentialForm.validate(async (valid) => {
        if (!valid) {
          return false;
        }

        this.submitLoading = true;
        try {
          const formData = {
            name: this.credentialForm.name,
            description: this.credentialForm.description,
            ip_whitelist: this.credentialForm.ip_whitelist,
            rate_limit: this.credentialForm.rate_limit,
          };

          if (this.credentialForm.expires_at) {
            formData.expires_at = Math.floor(this.credentialForm.expires_at / 1000);
          }

          let response;
          if (this.isEdit) {
            response = await apiCredentialService.updateCredential(
              this.currentCredentialId,
              formData,
              this
            );
          } else {
            response = await apiCredentialService.createCredential(formData, this);
          }

          if (response && response.status === 200) {
            this.dialogVisible = false;
            
            // 如果是创建，显示API Key
            if (!this.isEdit && response.data && response.data.api_key) {
              this.newApiKey = response.data.api_key;
              this.apiKeyDialogVisible = true;
            }
            
            this.loadCredentials();
          }
        } catch (error) {
          console.error("操作失败:", error);
        } finally {
          this.submitLoading = false;
        }
      });
    },

    /**
     * 切换状态
     */
    async handleToggleStatus(row) {
      const newStatus = row.status === 1 ? 0 : 1;
      const confirmText = newStatus === 1 
        ? this.$t('apicredentials.confirm_enable')
        : this.$t('apicredentials.confirm_disable');
      
      this.$confirm(confirmText, this.$t('waring.tips.text'), {
        confirmButtonText: this.$t('global.button.ok'),
        cancelButtonText: this.$t('global.button.cancel'),
        type: "warning",
      })
        .then(async () => {
          try {
            const response = await apiCredentialService.toggleCredentialStatus(
              row.id,
              newStatus,
              this
            );
            if (response && response.status === 200) {
              this.loadCredentials();
            }
          } catch (error) {
            console.error(`${action}失败:`, error);
          }
        })
        .catch(() => {});
    },

    /**
     * 删除凭证
     */
    handleDelete(row) {
      this.$confirm(this.$t('apicredentials.confirm_delete'), this.$t('waring.tips.text'), {
        confirmButtonText: this.$t('global.button.ok'),
        cancelButtonText: this.$t('global.button.cancel'),
        type: "warning",
      })
        .then(async () => {
          try {
            const response = await apiCredentialService.deleteCredential(row.id, this);
            if (response && response.status === 200) {
              this.loadCredentials();
            }
          } catch (error) {
            console.error("删除失败:", error);
          }
        })
        .catch(() => {});
    },

    /**
     * 关闭对话框
     */
    handleDialogClose() {
      this.$refs.credentialForm.resetFields();
    },

    /**
     * 复制到剪贴板
     */
    copyToClipboard(text) {
      if (!text) return;
      
      // 创建临时文本区域
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      
      try {
        document.execCommand("copy");
        this.$message({
          message: this.$t('global.copy.success'),
          type: "success",
        });
      } catch (err) {
        this.$message({
          message: this.$t('global.copy.fail'),
          type: "error",
        });
      } finally {
        document.body.removeChild(textarea);
      }
    },
  },
};
</script>

<style scoped>
.text-danger {
  color: #f56c6c;
}
.text-success {
  color: #67c23a;
}
</style>

